import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Bundle, ManagedNumber, OrderSummary, Product } from '@/api/types';
import { Banner, Card, ErrorNote, Spinner } from '@/components/Common';
import { StatusPill } from '@/components/StatusPill';
import { UsdCalculator } from '@/components/UsdCalculator';
import { isMeterProduct, isTeloneProduct } from '@/pages/PublicCheckoutPage';
import { useAuth } from '@/lib/auth';

/** {@code POST /api/v1/recharges} response shape (spec §11) — a superset of OrderSummary. */
type RechargeResponse = OrderSummary;

const PROVIDER_ORDER = ['Econet', 'NetOne', 'Telecel', 'Electricity', 'Telone', 'Other'];

// Zimbabwe mobile prefixes: 071 NetOne, 073 Telecel, 077/078 Econet.
const MSISDN_RE = /^0(71|73|77|78)\d{7}$/;

function normalizeMsisdn(raw: string): string {
  let v = raw.replace(/[^\d+]/g, '');
  if (v.startsWith('+263')) v = `0${v.slice(4)}`;
  else if (v.startsWith('263')) v = `0${v.slice(3)}`;
  return v;
}

function msisdnError(v: string): string | null {
  if (!v) return null;
  return MSISDN_RE.test(v) ? null : 'Enter a valid number, e.g. 077 292 9223';
}

function groupMsisdn(v: string): string {
  if (v.length < 10) return v;
  return `${v.slice(0, 3)} ${v.slice(3, 6)} ${v.slice(6, 10)}`;
}

const AMOUNT_CHIPS = ['1', '2', '5', '10', '20'];

function providerOf(p: Product): string {
  if (isMeterProduct(p)) return 'Electricity';
  if (isTeloneProduct(p)) return 'Telone';
  const first = (p.network ?? '').split(',')[0]?.trim();
  if (!first || first.toLowerCase() === 'allnetworks') return 'Other';
  return first;
}

/**
 * A company's own recharge screen — same provider-grid → product-grid → form flow as the
 * vendor till (VendorSellPage), minus the payment-method step: a company account is billed
 * directly against float via POST /api/v1/recharges, there's nothing to collect first. Adds
 * the saved-numbers picker (pre-filled from ?target=) and the post-recharge "save this number?"
 * offer, neither of which the vendor flow needs.
 */
export function NewRechargePage() {
  const { session } = useAuth();
  const [params] = useSearchParams();

  const [provider, setProvider] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [result, setResult] = useState<RechargeResponse | null>(null);
  const presetTarget = params.get('target') ?? '';

  const { data: catalogueData, isLoading: loadingCatalogue } = useQuery({
    queryKey: ['recharge-products'],
    queryFn: () => api.get<{ products: Product[] }>('/api/v1/products'),
    enabled: !!session?.merchantCode,
  });
  const products = (catalogueData?.products ?? []).filter((p) => p.enabled);
  const selected = products.find((p) => p.productId === selectedId) ?? null;

  const { data: savedNumbers } = useQuery({
    queryKey: ['managed-numbers'],
    queryFn: () => api.get<{ numbers: ManagedNumber[] }>('/api/v1/numbers'),
    enabled: !!session?.merchantCode,
  });
  const activeNumbers = (savedNumbers?.numbers ?? []).filter((n) => n.active);

  const searching = query.trim().length > 0;
  const searchResults = products.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()));

  const providerCounts = new Map<string, number>();
  products.forEach((p) => {
    const name = providerOf(p);
    providerCounts.set(name, (providerCounts.get(name) ?? 0) + 1);
  });
  const providerRank = (name: string) => {
    const i = PROVIDER_ORDER.indexOf(name);
    return i === -1 ? PROVIDER_ORDER.length - 1 : i; // unlisted providers sort just before "Other"
  };
  const providers = [...providerCounts.keys()].sort(
    (a, b) => providerRank(a) - providerRank(b) || a.localeCompare(b));

  const providerProducts = provider ? products.filter((p) => providerOf(p) === provider) : [];

  function startOver() {
    setResult(null);
    setSelectedId(null);
  }

  function backToProviders() {
    setProvider(null);
    setSelectedId(null);
  }

  if (!session?.merchantCode) {
    return (
      <Banner tone="warn">
        Your sign-in is platform-wide, not merchant-scoped, so it cannot place a recharge — the API
        requires a merchant identity (a merchant-scoped console user, or a merchant API key). Ask an
        admin to create a merchant-scoped user for you, or place the order via a merchant's API key
        instead.
      </Banner>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-sm font-semibold text-ink-800">New recharge</h1>

      {result ? (
        <RechargeResult result={result} onDone={startOver}
                         activeNumbers={activeNumbers} />
      ) : selected ? (
        <RechargeForm product={selected} onBack={() => setSelectedId(null)} onPlaced={setResult}
                      activeNumbers={activeNumbers} presetTarget={presetTarget} />
      ) : (
        <div className="card">
          <div className="p-4">
            <input className="input" value={query} onChange={(e) => { setQuery(e.target.value); setProvider(null); }}
                   placeholder="Search services…" />
          </div>

          {searching ? (
            <ProductGrid products={searchResults} loading={loadingCatalogue} onPick={setSelectedId}
                         empty="Nothing matches that search." />
          ) : provider ? (
            <>
              <div className="flex items-center gap-3 border-t border-ink-100 px-4 py-3">
                <button type="button" className="text-sm font-semibold text-ink-500 hover:text-ink-800"
                        onClick={backToProviders}>
                  ← Back
                </button>
                <h2 className="text-sm font-semibold text-ink-800">{provider}</h2>
              </div>
              <ProductGrid products={providerProducts} loading={loadingCatalogue} onPick={setSelectedId}
                           empty="No services under this provider." bordered={false} />
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3 border-t border-ink-100 p-4 sm:grid-cols-3 lg:grid-cols-4">
              {providers.map((name) => (
                <button key={name} type="button" onClick={() => setProvider(name)}
                        className="flex flex-col gap-1 rounded-lg border border-ink-200 bg-white p-5 text-left hover:border-ink-400 hover:shadow-sm">
                  <span className="text-base font-semibold text-ink-900">{name}</span>
                  <span className="text-xs text-ink-500">
                    {providerCounts.get(name)} service{providerCounts.get(name) === 1 ? '' : 's'}
                  </span>
                </button>
              ))}
              {loadingCatalogue && <div className="col-span-full"><Spinner /></div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProductGrid({ products, loading, onPick, empty, bordered = true }: {
  products: Product[];
  loading: boolean;
  onPick: (id: number) => void;
  empty: string;
  bordered?: boolean;
}) {
  return (
    <div className={`grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-4 ${bordered ? 'border-t border-ink-100' : ''}`}>
      {products.map((p) => (
        <button key={p.productId} type="button" onClick={() => onPick(p.productId)}
                className="flex flex-col gap-1 rounded-lg border border-ink-200 bg-white p-3 text-left hover:border-ink-400 hover:shadow-sm">
          <span className="text-[11px] font-bold uppercase text-ink-500">{providerOf(p)}</span>
          <span className="text-sm font-semibold text-ink-900">{p.name}</span>
          <span className="text-xs text-ink-500">
            {p.requiresProductCode ? 'Bundle' : 'Top-up'}{p.currency ? ` · ${p.currency}` : ''}
          </span>
        </button>
      ))}
      {loading && <div className="col-span-full"><Spinner /></div>}
      {!loading && products.length === 0 && (
        <p className="col-span-full py-8 text-center text-sm text-ink-500">{empty}</p>
      )}
    </div>
  );
}

type BulkResult = { number: ManagedNumber; status: 'pending' | 'done' | 'error'; message?: string };

function RechargeForm({ product, onBack, onPlaced, activeNumbers, presetTarget }: {
  product: Product;
  onBack: () => void;
  onPlaced: (result: RechargeResponse) => void;
  activeNumbers: ManagedNumber[];
  presetTarget: string;
}) {
  const queryClient = useQueryClient();
  const [productCode, setProductCode] = useState('');
  const [target, setTarget] = useState(presetTarget);
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState(false);
  const [clientReference, setClientReference] = useState('');
  const [touched, setTouched] = useState(false);
  // Bulk mode applies one product/amount to a whole batch of already-saved numbers — e.g. "top
  // up every delivery van" — instead of one target typed by hand.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkTargets, setBulkTargets] = useState<Set<string>>(new Set());
  const [bulkResults, setBulkResults] = useState<BulkResult[] | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  // Bundle products pick from a long list of mini options — that list gets its own step so the
  // recharge-number step isn't buried under dozens of bundle rows.
  const [step, setStep] = useState<'bundle' | 'details'>(product.requiresProductCode ? 'bundle' : 'details');

  const { data: fxData } = useQuery({
    queryKey: ['fx-rate'],
    queryFn: () => api.get<{ usdToZwgRate: number }>('/api/v1/fx-rate'),
    staleTime: 5 * 60_000,
    enabled: product.currency === 'ZWG',
  });

  const { data: bundlesData } = useQuery({
    queryKey: ['recharge-bundles', product.productId],
    queryFn: () => api.get<{ bundles: Bundle[] }>(`/api/v1/products/${product.productId}/bundles`),
    enabled: !!product.requiresProductCode,
  });
  const selectedBundle = bundlesData?.bundles.find((b) => b.productCode === productCode) ?? null;
  const matchingSaved = activeNumbers.find((n) => n.target === target);

  function pickBundle(code: string) {
    setProductCode(code);
    const bundle = bundlesData?.bundles.find((b) => b.productCode === code);
    if (bundle) setAmount(bundle.value);
    setStep('details');
  }

  const place = useMutation({
    mutationFn: () => api.post<RechargeResponse>('/api/v1/recharges', {
      productId: product.productId, target, amount,
      productCode: productCode || undefined,
      clientReference: clientReference || undefined,
    }),
    onSuccess: onPlaced,
  });

  const isMobileTarget = !isMeterProduct(product) && !isTeloneProduct(product);
  const targetLabel = isMeterProduct(product) ? 'Meter number'
    : isTeloneProduct(product) ? 'Telone account number' : 'Number to recharge';

  function setTargetValue(raw: string) {
    setTarget(isMobileTarget ? normalizeMsisdn(raw) : raw.replace(/\s+/g, ''));
  }

  function toggleBulkTarget(id: string) {
    setBulkTargets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const targetError = touched && !bulkMode && isMobileTarget ? msisdnError(target) : null;

  const min = product.minAmount ? Number(product.minAmount) : null;
  const max = product.maxAmount ? Number(product.maxAmount) : null;
  const amountNum = Number(amount);
  const amountValid = !!amount && amountNum > 0
    && (min == null || amountNum >= min) && (max == null || amountNum <= max);
  const amountRangeHint = min != null && max != null ? `${min}–${max}` : min != null ? `from ${min}` : max != null ? `up to ${max}` : null;

  const targetValid = bulkMode ? bulkTargets.size > 0 : !!target && (!isMobileTarget || MSISDN_RE.test(target));
  const canSubmit = targetValid && amountValid && (!product.requiresProductCode || !!productCode)
    && !place.isPending && !bulkRunning;

  async function runBulk() {
    const targets = activeNumbers.filter((n) => bulkTargets.has(n.id));
    setBulkRunning(true);
    setBulkResults(targets.map((n) => ({ number: n, status: 'pending' })));

    // Sequential, not parallel: each call must see the previous ones' spend so the daily-limit
    // check (OrderOrchestrator) stays correct, and a failure on one number must never look like
    // it affected any other.
    for (let i = 0; i < targets.length; i++) {
      try {
        await api.post<RechargeResponse>('/api/v1/recharges', {
          productId: product.productId, target: targets[i].target, amount,
          productCode: productCode || undefined,
          clientReference: `bulk-${Date.now()}-${targets[i].id}`,
        });
        setBulkResults((prev) => prev!.map((r, idx) => idx === i ? { ...r, status: 'done' } : r));
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed';
        setBulkResults((prev) => prev!.map((r, idx) => idx === i ? { ...r, status: 'error', message: msg } : r));
      }
    }
    setBulkRunning(false);
    queryClient.invalidateQueries({ queryKey: ['managed-numbers'] });
  }

  function submit() {
    setTouched(true);
    if (!canSubmit) return;
    if (bulkMode) runBulk(); else place.mutate();
  }

  if (step === 'bundle') {
    return (
      <div className="card">
        <div className="card-header flex items-center gap-3">
          <button type="button" className="text-sm font-semibold text-ink-500 hover:text-ink-800" onClick={onBack}>
            ← Back
          </button>
          <h2 className="text-sm font-semibold text-ink-800">
            {product.name}{product.network ? ` — ${product.network}` : ''}
          </h2>
        </div>
        <div className="p-4">
          <span className="label">Choose a bundle</span>
          <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {(bundlesData?.bundles ?? []).map((b) => (
              <button key={b.productCode} type="button" onClick={() => pickBundle(b.productCode)}
                      className="min-h-[48px] rounded-lg border border-ink-200 p-2 text-left text-xs hover:border-ink-400">
                <div className="font-semibold">{b.name}</div>
                <div className="text-ink-500">{b.currency} {b.value}</div>
              </button>
            ))}
            {!bundlesData && <Spinner label="Loading bundles" />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header flex items-center gap-3">
        <button type="button" className="text-sm font-semibold text-ink-500 hover:text-ink-800"
                onClick={() => (product.requiresProductCode ? setStep('bundle') : onBack())}>
          ← Back
        </button>
        <h2 className="text-sm font-semibold text-ink-800">
          {product.name}{product.network ? ` — ${product.network}` : ''}
        </h2>
      </div>
      <div className="space-y-4 p-4">
        {product.requiresProductCode && selectedBundle && (
          <div className="flex items-center justify-between rounded-lg bg-ink-50 px-3 py-2 text-sm">
            <div>
              <div className="font-semibold text-ink-900">{selectedBundle.name}</div>
              <div className="text-ink-500">{selectedBundle.currency} {selectedBundle.value}</div>
            </div>
            <button type="button" className="text-xs font-semibold text-ink-600 hover:text-ink-900"
                    onClick={() => setStep('bundle')}>
              Change
            </button>
          </div>
        )}

        {activeNumbers.length > 1 && bulkResults === null && (
          <label className="flex items-center gap-1.5 text-xs text-ink-600">
            <input type="checkbox" checked={bulkMode}
                   onChange={(e) => { setBulkMode(e.target.checked); setTouched(false); }} />
            Recharge multiple saved numbers at once
          </label>
        )}

        {bulkResults !== null ? (
          <div>
            <p className="text-sm text-ink-600">
              {bulkResults.filter((r) => r.status !== 'pending').length} of {bulkResults.length} done
              {' — '}{bulkResults.filter((r) => r.status === 'done').length} succeeded
              {bulkResults.some((r) => r.status === 'error') && (
                <span className="text-red-700">, {bulkResults.filter((r) => r.status === 'error').length} failed</span>
              )}.
            </p>
            <ul className="mt-3 max-h-80 space-y-1 overflow-y-auto">
              {bulkResults.map((r) => (
                <li key={r.number.id} className="flex items-center justify-between rounded border border-ink-100 px-2 py-1.5 text-sm">
                  <div>
                    <span className="font-medium">{r.number.label}</span>
                    <span className="ml-2 mono text-xs text-ink-500">{r.number.target}</span>
                  </div>
                  {r.status === 'pending' && <span className="text-xs text-ink-400">…</span>}
                  {r.status === 'done' && <span className="text-xs font-semibold text-emerald-700">Placed</span>}
                  {r.status === 'error' && (
                    <span className="text-xs font-semibold text-red-700" title={r.message}>Failed</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : bulkMode ? (
          <div>
            <span className="label">Numbers to recharge</span>
            <ul className="mt-1 max-h-60 space-y-1 overflow-y-auto rounded-lg border border-ink-200 p-2">
              {activeNumbers.map((n) => (
                <li key={n.id}>
                  <label className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-ink-50">
                    <input type="checkbox" checked={bulkTargets.has(n.id)}
                           onChange={() => toggleBulkTarget(n.id)} />
                    <span className="font-medium text-ink-800">{n.label}</span>
                    <span className="mono text-xs text-ink-500">{n.target}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            {activeNumbers.length > 0 && (
              <label className="block">
                <span className="label">Saved numbers</span>
                <select className="input mt-1" value={matchingSaved?.id ?? ''}
                        onChange={(e) => {
                          const picked = activeNumbers.find((n) => n.id === e.target.value);
                          if (picked) setTargetValue(picked.target);
                        }}>
                  <option value="">Pick a saved number, or type one below…</option>
                  {activeNumbers.map((n) => (
                    <option key={n.id} value={n.id}>{n.label} — {n.target}</option>
                  ))}
                </select>
              </label>
            )}

            <label className="block">
              <span className="label">{targetLabel}</span>
              <input
                className={`input mt-1 h-12 text-base ${targetError ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''}`}
                type="tel" inputMode="numeric" value={target}
                onChange={(e) => setTargetValue(e.target.value)}
                onBlur={() => setTouched(true)}
                placeholder={isMobileTarget ? '0772929223' : 'e.g. 12345678901'} autoFocus />
              {targetError && <p className="mt-1 text-xs text-red-600">{targetError}</p>}
              {matchingSaved && !targetError && (
                <span className="mt-1 block text-xs text-ink-500">Saved as "{matchingSaved.label}".</span>
              )}
            </label>
          </>
        )}

        {!product.requiresProductCode && (
          <div>
            <span className="label">Amount{product.currency ? ` (${product.currency})` : ''}</span>
            {!customAmount ? (
              <div className="mt-1 grid grid-cols-5 gap-2">
                {AMOUNT_CHIPS.map((chip) => (
                  <button key={chip} type="button" onClick={() => setAmount(chip)}
                          className={`h-12 rounded-lg border text-sm font-semibold ${
                            amount === chip && !customAmount
                              ? 'border-ink-900 bg-ink-900 text-white' : 'border-ink-200 hover:border-ink-400'
                          }`}>
                    {chip}
                  </button>
                ))}
                <button type="button" onClick={() => { setCustomAmount(true); setAmount(''); }}
                        className="h-12 rounded-lg border border-ink-200 text-xs font-semibold text-ink-600 hover:border-ink-400">
                  Other
                </button>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <input className="input h-12 text-base tabular" inputMode="decimal" value={amount}
                       onChange={(e) => setAmount(e.target.value)} onBlur={() => setTouched(true)}
                       placeholder="e.g. 5.00" autoFocus />
                <button type="button" onClick={() => { setCustomAmount(false); setAmount(''); }}
                        className="text-xs font-semibold text-ink-500 hover:text-ink-800">
                  Use chips
                </button>
              </div>
            )}
            {touched && !!amount && !amountValid && (
              <p className="mt-1 text-xs text-red-600">
                Enter an amount{amountRangeHint ? ` ${amountRangeHint}` : ' greater than 0'}.
              </p>
            )}
            <UsdCalculator rate={fxData?.usdToZwgRate} currency={product.currency}
                           onApply={(zwg) => { setCustomAmount(true); setAmount(zwg); }} />
          </div>
        )}

        {!bulkMode && bulkResults === null && (
          <label className="block">
            <span className="label">Client reference (optional)</span>
            <input className="input mt-1" value={clientReference}
                   placeholder="Your own idempotency key — resubmitting it replays the same order"
                   onChange={(e) => setClientReference(e.target.value)} />
          </label>
        )}
      </div>

      {place.isError && <div className="px-4"><ErrorNote error={place.error} /></div>}

      <div className="space-y-2 border-t border-ink-100 p-4">
        {bulkResults !== null ? (
          <button type="button" onClick={onBack} disabled={bulkRunning}
                  className="h-12 w-full rounded-lg bg-ink-900 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-ink-300">
            {bulkRunning ? 'Working…' : 'Done'}
          </button>
        ) : (
          <>
            {targetValid && amountValid && (
              <p className="text-center text-sm text-ink-600">
                {bulkMode
                  ? `Recharge ${product.currency ?? ''} ${amount} to ${bulkTargets.size} selected number${bulkTargets.size === 1 ? '' : 's'}`
                  : `Recharge ${product.currency ?? ''} ${amount} to ${isMobileTarget ? groupMsisdn(target) : target}`}
              </p>
            )}
            <button type="button" onClick={submit} disabled={!canSubmit}
                    className="h-12 w-full rounded-lg bg-ink-900 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-ink-300">
              {place.isPending || bulkRunning ? 'Placing…'
                : bulkMode ? `Recharge ${bulkTargets.size || ''} number${bulkTargets.size === 1 ? '' : 's'}`
                : amountValid ? `Recharge ${product.currency ?? ''} ${amount}` : 'Place recharge'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function RechargeResult({ result, onDone, activeNumbers }: {
  result: RechargeResponse;
  onDone: () => void;
  activeNumbers: ManagedNumber[];
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saveNumberLabel, setSaveNumberLabel] = useState('');
  const alreadySaved = activeNumbers.some((n) => n.target === result.target);

  const saveNumber = useMutation({
    mutationFn: () => api.post('/api/v1/numbers', { target: result.target, label: saveNumberLabel }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['managed-numbers'] }),
  });

  return (
    <div className="space-y-4">
      <div className={`card border p-5 ${
        result.status === 'UNKNOWN' ? 'border-amber-300 bg-amber-50 text-amber-900'
          : 'border-emerald-300 bg-emerald-50 text-emerald-900'
      }`}>
        <div className="flex items-center gap-2">
          <StatusPill status={result.status} />
          <span className="mono text-sm">{result.agentReference}</span>
        </div>
        {result.message && <p className="mt-2 text-sm">{result.message}</p>}
        {result.status === 'UNKNOWN' && (
          <p className="mt-2 text-sm">
            The outcome is unresolved upstream. Do not resubmit — the reconciler will settle this
            order; open its detail page to watch it resolve.
          </p>
        )}
        <div className="mt-4 flex gap-2">
          <button type="button" className="btn-secondary" onClick={() => navigate(`/orders/${result.id}`)}>
            View order
          </button>
          <button type="button" className="btn-primary" onClick={onDone}>Recharge another</button>
        </div>
      </div>

      {!alreadySaved && !saveNumber.isSuccess && (
        <Card title="Save this number?">
          <div className="flex items-end gap-2 p-4">
            <label className="flex-1">
              <span className="label">Label</span>
              <input className="input mt-1" value={saveNumberLabel}
                     placeholder="e.g. Reception desk"
                     onChange={(e) => setSaveNumberLabel(e.target.value)} />
            </label>
            <button type="button" className="btn-primary" disabled={!saveNumberLabel || saveNumber.isPending}
                    onClick={() => saveNumber.mutate()}>
              {saveNumber.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
          {saveNumber.isError && <ErrorNote error={saveNumber.error} />}
        </Card>
      )}
      {saveNumber.isSuccess && (
        <Banner tone="info">Saved — it'll show up under Numbers and in the picker next time.</Banner>
      )}
    </div>
  );
}
